import { X, Trash2, CreditCard, ShoppingBag } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Separator } from '@/components/ui/separator'
import { useCart } from '@/lib/cart'
import { useNavigate, useLocation } from 'react-router-dom'
import { formatCurrency } from '@/lib/utils'
import type { CartItem } from '@/types/cart'
import type { FlightResult, MultiSliceFlight, Hotel } from '@/types/travel'

// Discriminate on `type === 'multi-slice'` (same pattern as
// flight-card.tsx:18). Do NOT use `'totalPrice' in flight` here —
// expandable-flight-card.tsx:450 mutates single-slice flights with a
// copied `totalPrice` field at add-to-cart time, which makes the
// `in` check produce false positives and crash on flight.slices[0].
const isMultiSliceFlight = (flight: FlightResult): flight is MultiSliceFlight =>
  (flight as MultiSliceFlight).type === 'multi-slice'

export function CartOverlay() {
  const { cart, removeFromCart, clearCart, isOpen, closeCart } = useCart()
  const navigate = useNavigate()
  const { pathname } = useLocation()
  // Don't allow clearing cart while on checkout (avoids accidental empty during purchase)
  const isCheckoutPage = pathname.startsWith('/checkout')

  const handleCheckout = async () => {
    if (cart.items.length === 0) return

    // Navigate to checkout page instead of direct Stripe
    closeCart()
    navigate('/checkout')
  }

  const getItemName = (cartItem: CartItem): string => {
    if (cartItem.type === 'flight') {
      const flight = cartItem.item as FlightResult
      if (isMultiSliceFlight(flight)) {
        const firstSlice = flight.slices[0]
        const lastSlice = flight.slices[flight.slices.length - 1]

        // Check if it's a round trip (returns to origin)
        if (firstSlice.origin === lastSlice.destination) {
          return `${firstSlice.origin} - ${firstSlice.destination} (Round Trip)`
        } else {
          return `${firstSlice.origin} → ${lastSlice.destination} (${flight.slices.length} flights)`
        }
      } else {
        return `${flight.origin} → ${flight.destination}`
      }
    } else {
      const hotel = cartItem.item as Hotel
      return hotel.name
    }
  }

  const getItemDescription = (cartItem: CartItem): string => {
    if (cartItem.type === 'flight') {
      const flight = cartItem.item as FlightResult
      if (isMultiSliceFlight(flight)) {
        return flight.slices.map((slice) => {
          const depTime = new Date(slice.departureTime).toLocaleTimeString('en-US', {
            hour: '2-digit',
            minute: '2-digit',
            hour12: false
          })
          const arrTime = new Date(slice.arrivalTime).toLocaleTimeString('en-US', {
            hour: '2-digit',
            minute: '2-digit',
            hour12: false
          })
          return `${slice.airline} ${slice.flightNumber}: ${slice.origin} ${depTime} → ${slice.destination} ${arrTime}`
        }).join(' • ')
      } else {
        const depTime = new Date(flight.departureTime).toLocaleTimeString('en-US', {
          hour: '2-digit',
          minute: '2-digit',
          hour12: false
        })
        const arrTime = new Date(flight.arrivalTime).toLocaleTimeString('en-US', {
          hour: '2-digit',
          minute: '2-digit',
          hour12: false
        })
        return `${flight.airline} ${flight.flightNumber}: ${depTime} → ${arrTime} (${flight.duration})`
      }
    } else {
      const hotel = cartItem.item as Hotel
      return hotel.location
    }
  }

  if (!isOpen) return null

  return (
    <>
      {/* Backdrop — v6 overlay treatment (navy 55% + blur). */}
      <div
        className="fixed inset-0 bg-[rgba(15,25,35,0.55)] backdrop-blur-sm z-40"
        onClick={closeCart}
      />
      
      {/* Cart overlay */}
      <div className={`
        fixed top-0 right-0 h-full w-full max-w-md bg-background shadow-2xl z-50
        transform transition-transform duration-300 ease-in-out
        ${isOpen ? 'translate-x-0' : 'translate-x-full'}
      `}>
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b">
            <div className="flex items-center gap-2">
              <ShoppingBag className="h-5 w-5" />
              <h2 className="text-lg font-semibold">Shopping Cart</h2>
              {cart.itemCount > 0 && (
                <span className="text-sm text-muted-foreground">
                  ({cart.itemCount} item{cart.itemCount !== 1 ? 's' : ''})
                </span>
              )}
            </div>
            <Button variant="ghost" size="icon" onClick={closeCart}>
              <X className="h-4 w-4" />
            </Button>
          </div>

          {/* Cart items */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {cart.items.length === 0 ? (
              <div className="text-center py-8">
                <ShoppingBag className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">Your cart is empty</p>
                <p className="text-sm text-muted-foreground mt-1">
                  Add flights and hotels to get started
                </p>
              </div>
            ) : (
              cart.items.map((item) => (
                <Card key={`${item.type}-${item.id}`} className="overflow-hidden">
                  <CardContent className="p-4">
                    <div className="flex justify-between items-start mb-2">
                      <div className="flex-1">
                        <h3 className="font-medium text-sm line-clamp-2">
                          {getItemName(item)}
                        </h3>
                        <p className="text-xs text-muted-foreground mt-1">
                          {getItemDescription(item)}
                        </p>
                        {item.type === 'flight' && (() => {
                          const flight = item.item as FlightResult;
                          if (flight.passengers) {
                            const parts = [];
                            if (flight.passengers.adults > 0) {
                              parts.push(`${flight.passengers.adults} adult${flight.passengers.adults !== 1 ? 's' : ''}`);
                            }
                            if (flight.passengers.children && flight.passengers.children > 0) {
                              parts.push(`${flight.passengers.children} child${flight.passengers.children !== 1 ? 'ren' : ''}`);
                            }
                            if (parts.length > 0) {
                              return (
                                <p className="text-xs text-muted-foreground mt-0.5">
                                  {parts.join(', ')}
                                </p>
                              );
                            }
                          }
                          return null;
                        })()}
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => removeFromCart(item.id)}
                        className="h-6 w-6 text-muted-foreground hover:text-destructive"
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>

                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium">
                          {(() => {
                            if (item.type === 'flight') {
                              const flight = item.item as FlightResult;
                              if (flight.passengers) {
                                const totalPassengers = flight.passengers.adults + (flight.passengers.children || 0);
                                return `${totalPassengers} passenger${totalPassengers !== 1 ? 's' : ''}`;
                              }
                            }
                            return `${item.quantity} ${item.type}${item.quantity !== 1 ? 's' : ''}`;
                          })()}
                        </span>
                      </div>
                      <div className="text-right">
                        <p className="font-medium text-sm">
                          {formatCurrency(item.price * item.quantity)}
                        </p>
                        {(() => {
                          if (item.type === 'flight') {
                            const flight = item.item as FlightResult;
                            if (flight.passengers) {
                              const totalPassengers = flight.passengers.adults + (flight.passengers.children || 0);
                              if (totalPassengers > 1) {
                                return (
                                  <p className="text-xs text-muted-foreground">
                                    {formatCurrency(item.price / totalPassengers)} per person
                                  </p>
                                );
                              }
                            }
                          }
                          if (item.quantity > 1) {
                            return (
                              <p className="text-xs text-muted-foreground">
                                {formatCurrency(item.price)} each
                              </p>
                            );
                          }
                          return null;
                        })()}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>

          {/* Footer */}
          {cart.items.length > 0 && (
            <div className="border-t p-4 space-y-4">
              {/* Summary */}
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span>Flights ({cart.flightCount})</span>
                  <span className="text-muted-foreground">
                    {cart.flightCount > 0 ? 'Included' : 'None'}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span>Hotels ({cart.hotelCount})</span>
                  <span className="text-muted-foreground">
                    {cart.hotelCount > 0 ? 'Included' : 'None'}
                  </span>
                </div>
                <Separator />
                <div className="flex justify-between font-semibold">
                  <span>Total</span>
                  <span>{formatCurrency(cart.totalPrice)}</span>
                </div>
              </div>

              {/* Actions */}
              <div className="space-y-2">
                <Button
                  onClick={handleCheckout}
                  className="w-full"
                >
                  <div className="flex items-center gap-2">
                    <CreditCard className="h-4 w-4" />
                    Checkout
                  </div>
                </Button>
                
                {!isCheckoutPage && (
                  <Button
                    variant="outline"
                    onClick={clearCart}
                    className="w-full"
                  >
                    Clear Cart
                  </Button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  )
} 